import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import * as yup from "yup";
import MCard from "components/Shared/MCard.tsx";
import MField from "components/Shared/MField";
import OrderStepButtons from "components/WebOrderV2/OrderStepButtons";
import {
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_CREDIT_CARD,
  DATA_TYPE_NUMBER_UNFORMATTED,
  DATA_TYPE_NUMBER_STRING,
} from "typeConstants/dataTypes";
import { PAY_NOW, DEFER_PAYMENT, MUELLER_INVOICE } from "typeConstants/webOrder";

const Payment = props => {
  const getPaymentOption = () => {
    if (props.paymentOptions.length === 1){
      return props.paymentOptions[0];
    }
    return props.data?.paymentOption;
  };

  const [paymentMethodOption, setPaymentMethodOption] = useState(getPaymentOption());
  const [showExpired, setShowExpired] = useState(false);
  const [showAuthorizeValidation, setShowAuthorizeValidation] = useState(false);

  const mCardControlMethods = useRef({});
  const mounted = useRef(true);
  const selfManagedEditData = useRef({});

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    mCardControlMethods.current.refreshEditData();
  }, [paymentMethodOption]);

  const setControlMethods = () => {
    return cm => (mCardControlMethods.current = cm);
  };

  // Authorization checkbox fields are managed outside of MCard for better formatting
  const updateSelfManagedEditData = (key, value) => {
    selfManagedEditData.current[key] = value;
  };

  const onPrevious = () => {
    let editedData = mCardControlMethods.current.getEditedDataObject();
    // Need to store the whole option object to preserve selection
    editedData.paymentOption = paymentMethodOption;
    editedData = {...editedData, ...selfManagedEditData.current};
    props.onPrevious(editedData);
  };

  const onNext = () => {
    let isValid = mCardControlMethods.current.runValidation(true);
    let editedData = mCardControlMethods.current.getEditedDataObject();
      
    // Check expiration
    let currentMonth = new Date().getMonth();
    let currentYear = new Date().getFullYear();

    if (editedData.expirationMonth <= currentMonth && editedData.expirationYear === currentYear) {
      setShowExpired(true);
      isValid = false;
    } else if (showExpired) {
      setShowExpired(false);
    }

    // Check authorize checkbox
    switch (paymentMethodOption.value) {
      case PAY_NOW:
        if (!selfManagedEditData.current.authorizeCardHold && !props.data.authorizeCardHold) {
          setShowAuthorizeValidation(true);
          isValid = false;
        }
        break;
      case DEFER_PAYMENT:
        if (!selfManagedEditData.current.authorizeNotification && !props.data.authorizeNotification) {
          setShowAuthorizeValidation(true);
          isValid = false;
        }
        break;
    }
     
    if (isValid) {
      editedData.paymentOption = paymentMethodOption;
      editedData = {...editedData, ...selfManagedEditData.current};
      props.onNext(editedData);
    }
  };

  const getPaymentOptions = () => {
    // Remove option if email wasn't provided
    if (!props.data.homeownerEmailAddress) {
      return props.paymentOptions.filter(p => p.value !== DEFER_PAYMENT);
    }
    return props.paymentOptions;
  };

  // Indicate when deferred payment is unavailable due to lack of email
  const paymentLabel = props.paymentOptions.some(p => p.value === DEFER_PAYMENT) && !props.data.homeownerEmailAddress
    ? "Payment*" : "Payment";

  const paymentOptionField = [
    {
      key: "paymentOption",
      dataType: DATA_TYPE_DROPDOWN2,
      label: paymentLabel,
      value: props.data.paymentOption ?? paymentMethodOption,
      dataTypeOptions: { options: getPaymentOptions() },
      validation: yup.string().required("Payment method is required"),
      onChange: option => setPaymentMethodOption(option),
      placeholder: "Select... (required)",
    },
  ];

  const currentYear = new Date().getFullYear();

  const creditCardFields = [
    {
      key: "cardNumber",
      dataType: DATA_TYPE_CREDIT_CARD,
      label: "Card Number",
      value: props.data.cardNumber ? Number(props.data.cardNumber) : 0,
      // Min card length is 15 digits, max is 16. Discover starts with 6 and no other cards are higher
      validation: yup.number().min(4000000000000, "Invalid card number").max(7000000000000000, "Invalid card number").required("Card Number is required"),
      placeholder: "Required",
    },
    {
      key: "cardHolderName",
      label: "Cardholder Name",
      value: props.data.cardHolderName,
      validation: yup.string().required("Cardholder Name is required"),
      placeholder: "Required",
    },
    {
      key: "expirationMonth",
      dataType: DATA_TYPE_NUMBER_STRING,
      label: "Expiration Month (MM)",
      value: props.data.expirationMonth,
      validation: yup.string().min(2, "Invalid month (MM)").max(2, "Invalid month (MM)").required("Expiration Month (MM) is required"),
      placeholder: "Required",
    },
    {
      key: "expirationYear",
      dataType: DATA_TYPE_NUMBER_UNFORMATTED,
      label: "Expiration Year (YYYY)",
      value: props.data.expirationYear,
      validation: yup.number("Expiration Year (YYYY) is required").min(currentYear, "Invalid year (YYYY)").max(2099, "Invalid year (YYYY)").required("Expiration Year (YYYY) is required"),
      placeholder: "Required",
    },
    {
      key: "cardCVC",
      dataType: DATA_TYPE_NUMBER_STRING,
      label: "Card CVC",
      value: props.data.cardCVC,
      validation: yup.string().min(3, "Invalid CVC").max(4, "Invalid CVC").required("CVC is required"),
      placeholder: "Required",
    },
  ];

  const getDeferPaymentDescription = () => {
    // eslint-disable-next-line react/prop-types
    let borrowerEmail = props.data.homeownerEmailAddress;
    return (
      <div className="web-order-payment-description">
        <p><b>The order requires payment authorization prior to work being performed.</b></p>
        <p>Once "Place Order" is clicked the order will be placed in "Order Payment Pending" status
          and an email notification will be sent to the borrower email provided: <b>{borrowerEmail}</b>.</p>
        <p>If payment authorization is not provided within 24 hours a follow up email notification will be sent.</p>
        <p>If no payment authorization is provided after 10 days then the order will be automatically cancelled.</p>
        <br />
        <div>
          <MField
            id="authorize-notification"
            dataType={DATA_TYPE_BOOLEAN}
            onChange={value => updateSelfManagedEditData("authorizeNotification", value)}
            // eslint-disable-next-line react/prop-types
            value={props.data.authorizeNotification}
            ariaLabel="Check this box to authorize the payment notification email"
          />
          <p>Please check this box to authorize the payment notification email.</p>
        </div>
        {showAuthorizeValidation && <p className="web-order-warning-text">Required</p>}
      </div>
    );
  };

  const getPayNowDescription = () => {
    return (
      <div className="web-order-payment-description">
        {showExpired && <p className="web-order-warning-text">Credit Card expired.</p>}
        <p>The following credit cards are accepted: Visa, MasterCard, Discover and American Express.</p>
        <div>
          <MField
            id="authorize-hold"
            dataType={DATA_TYPE_BOOLEAN}
            onChange={value => updateSelfManagedEditData("authorizeCardHold", value)}
            // eslint-disable-next-line react/prop-types
            value={props.data.authorizeCardHold}
            ariaLabel="I authorize a hold to be placed on the card provided for the amount listed above."
          />
          <p>I authorize a hold to be placed on the card provided for the amount listed above.</p>
        </div>
        {showAuthorizeValidation && <p className="web-order-warning-text">Required</p>}
      </div>);
  };

  const getPaymentOptionDescription = () => {
    let selectedPayment = paymentMethodOption?.value;
    // eslint-disable-next-line react/prop-types
    if (props.paymentOptions.length === 1) {
      // eslint-disable-next-line react/prop-types
      selectedPayment = props.paymentOptions[0].value;
    }
    switch (selectedPayment) {
      case PAY_NOW:
        return getPayNowDescription();
      case DEFER_PAYMENT:
        return getDeferPaymentDescription();
      case MUELLER_INVOICE:
        return (
          <p>This order will be invoiced per the terms in your contract agreement.</p>
        );
      default:
        return null;
    }
  };

  const getFormFields = () => {
    let fields = [];

    if (props.paymentOptions.length > 1) {
      fields.push(...paymentOptionField);
    }
    if (paymentMethodOption?.value === PAY_NOW) {
      fields.push(...creditCardFields);
    }
    return fields;
  };

  return (
    <>
      <MCard
        title={"Payment"}
        getControlMethods={setControlMethods("payment")}
        fields={getFormFields()}
        onlyEditable
      >
        {getPaymentOptionDescription()}
      </MCard>
      {(props.paymentOptions.some(p => p.value === DEFER_PAYMENT) && !props.data.homeownerEmailAddress) 
        && <p className="web-order-payment-deferred-warning">*No borrower email provided, unable to provide deferred payment option</p>}
      <OrderStepButtons
        currentStep={props.currentStep}
        onPrevious={onPrevious}
        onNext={onNext}
        nextTitle="Place Order"
      />
    </>
  );
};

export default Payment;

Payment.propTypes = {
  currentStep: PropTypes.number,  // Current step in order process, needed for OrderStepButtons
  data: PropTypes.object, // Data for MCard fields
  onNext: PropTypes.func, // Func to call when fields are valid to move to the next step
  onPrevious: PropTypes.func, // Func to call to move to previous step
  terminology: PropTypes.string, // Insurance or Lender, see typeConstants/webOrder.js
  pageLayout: PropTypes.string, // LenderAppraisal, LenderNonAppraisal, or Insurane, see typeConstants/webOrder.js
  accountFeatures: PropTypes.object, // Enabled customer features for selected account
  paymentOptions: PropTypes.array,  // Available payment options based on settings
};